import { createMuiTheme } from '@material-ui/core/styles';
import createPalette from '@material-ui/core/styles/createPalette';
import { pink } from '@material-ui/core/colors';

// https://material-ui.com/customization/theming/#customizing-all-instances-of-a-component-type
// https://material-ui.com/customization/color/

const palette = createPalette({
    type: 'light',
    primary: {
        light: '#FFFFFF',
        main: '#E3E2DD',
        dark: '#CDCDCA',
        contrastText: '#859254',
        negative: '#EE4248',
        manatee: '#959595',
        dim: '#707070',
        grey: '#464646'
    },
    secondary: {
        main: '#CA5F46',
        contrastText: '#FFFFFF',
        dark: '#AA4C37',
        light: '#E9D3CC',
        iconHoverColor: '#CCCCCC',
        actionHoverColor: '#f39305',
    },
    userLayer: {
        main: '#e58e0d',
        contrastText: '#FFFFFF'
    },
    error: {
        ...pink
    },
    abmiColor: '#859254'
});

const typography = {
    button: {
        fontSize: 11,
        // fontWeight: 600,
        letterSpacing: '0.01em'
    },
    h1: {
        fontWeight: 600,
        letterSpacing: '-0.02em'
    },
    h2: {
        fontWeight: 600,
        letterSpacing: '-0.02em'
    },
    h3: {
        fontWeight: 600,
        letterSpacing: '-0.01em'
    },
    h4: {
        fontWeight: 600
    },
    h5: {
        // fontWeight: 600
    },
    h6: {
        // fontWeight: 600
    },
    subtitle1: {
        fontWeight: 500
    },
    subtitle2: {
        fontWeight: 600
    },
    body1: {
        fontSize: 15,
        lineHeight: 1.55
    },
    body2: {
        fontSize: 13,
        lineHeight: 1.5
    }
};

const actionBtn = {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 14
};

const topBarHeight = 100;
const radius = 12;
const commonBorder = '1px solid rgba(0, 0, 0, 0.08)';
const transition = 'background-color 150ms ease, color 150ms ease, box-shadow 150ms ease, border-color 150ms ease';

// MUI expects 25 elevation levels; these are softer and more diffuse than the Material defaults
const shadows = [
    'none',
    '0 1px 2px rgba(16, 24, 40, 0.06)',
    '0 1px 3px rgba(16, 24, 40, 0.08), 0 1px 2px rgba(16, 24, 40, 0.04)',
    '0 2px 6px rgba(16, 24, 40, 0.08), 0 1px 2px rgba(16, 24, 40, 0.04)',
    '0 4px 10px rgba(16, 24, 40, 0.08), 0 2px 4px rgba(16, 24, 40, 0.04)',
    ...Array.from({ length: 4 }, () => '0 8px 20px rgba(16, 24, 40, 0.10), 0 2px 6px rgba(16, 24, 40, 0.05)'),
    ...Array.from({ length: 8 }, () => '0 12px 28px rgba(16, 24, 40, 0.12), 0 4px 8px rgba(16, 24, 40, 0.05)'),
    ...Array.from({ length: 8 }, () => '0 20px 40px rgba(16, 24, 40, 0.14), 0 6px 12px rgba(16, 24, 40, 0.06)')
];

const theme = createMuiTheme({
    // material-ui
    palette: palette,
    typography: typography,
    shape: {
        borderRadius: radius
    },
    shadows: shadows,
    topBar: {
        height: topBarHeight,
        iconHoverColor: '#668833',
        color: '#859254'
    },
    sidePanel: {
        width: 400,
        marginTop: topBarHeight,
        height: `calc(100vh - ${topBarHeight}px)`
    },
    layerCard: {
        backgroundColor: 'transparent',
        borderRadius: 0,
        borderBottom: commonBorder,
        boxShadow: 'none',
        transition: transition
    },
    actionBtn: actionBtn,
    cancelBtn: {
        ...actionBtn,
        color: palette.primary.dim
    },
    removeBtn: {
        ...actionBtn,
        color: palette.error.main,
    },
    commonSpacing: {
        paddingLeft: 16,
        paddingRight: 12
    },
    commonPanel: {
        borderBottom: commonBorder,
        minHeight: 40
    },
    commonAlignment: {
        display: 'flex',
        alignItems: 'center'
    },
    menuIcon: {
        marginRight: 7,
        marginLeft: -3
    },
    overrides: {
        MuiCardHeader: {
            root: {
                padding: 0
            }
        },
        MuiButton: {
            root: {
                textTransform: 'none',
                borderRadius: radius,
                transition: transition
            },
            text: {
                '&:focus': {
                    color: palette.secondary.main
                },
                '&:hover': {
                    backgroundColor: 'transparent'
                }
            },
            textSecondary: {
                '&:hover': {
                    backgroundColor: 'transparent'
                }
            },
            contained: {
                'textTransform': 'none',
                'boxShadow': 'none',
                '&:hover': {
                    boxShadow: shadows[2]
                },
                '&:active': {
                    boxShadow: 'none'
                },
                '&:disabled': {
                    color: palette.primary.light
                }
            },
            outlined: {
                textTransform: 'none'
            }
        },
        MuiIconButton: {
            root: {
                'transition': transition,
                '&:hover': {
                    backgroundColor: 'transparent'
                }
            },
            colorSecondary: {
                '&:hover': {
                    backgroundColor: 'transparent'
                }
            }
        },
        MuiPaper: {
            rounded: {
                borderRadius: radius
            }
        },
        MuiMenu: {
            paper: {
                border: commonBorder,
                boxShadow: shadows[8]
            }
        },
        MuiPopover: {
            paper: {
                borderRadius: radius
            }
        },
        MuiDialog: {
            paper: {
                borderRadius: radius * 1.5
            }
        },
        MuiInput: {
            underline: {
                '&:after': {
                    borderBottomColor: palette.secondary.main
                }
            }
        },
        MuiOutlinedInput: {
            root: {
                borderRadius: radius
            }
        },
        MuiInputLabel: {
            root: {
                '&$focused': {
                    color: palette.secondary.main
                },
                '&$error': {
                    color: palette.error.main
                }
            },
            shrink: {
                /*increasing the scaling factor here allows for slightly bigger font than the default one*/
                transform: 'translate(0, 1.5px) scale(0.8)'
            }
        },
        MuiLink: {
            root: {
                color: palette.secondary.main
            }
        },
        MuiExpansionPanelSummary: {
            root: {
                padding: '0 12px',
                minHeight: '44px',
                '&$expanded': {
                    minHeight: '44px'
                },
                '& button': {
                    padding: '8px 12px'
                },
                '& p': {
                    padding: '4px 0'
                }
            },
            content: {
                margin: 0,
                alignItems: 'center',
                '&$expanded': {
                    margin: 0
                }
            }
        },
        MuiExpansionPanel: {
            root: {
                '&$expanded': {
                    margin: 0
                }
            }
        },
        MuiTooltip: {
            tooltip: {
                fontSize: 13,
                lineHeight: 1.4,
                padding: '6px 10px',
                borderRadius: radius - 2,
                backgroundColor: 'rgba(33, 37, 41, 0.92)',
                boxShadow: shadows[3]
            }
        },
        MuiMenuItem: {
            root: {
                fontSize: 14,
                minHeight: '32px!important',
                paddingTop: 0,
                paddingBottom: 0,
                margin: '0 4px',
                borderRadius: radius - 2,
                transition: transition
            }
        },
        MuiDivider: {
            root: {
                backgroundColor: 'rgba(0, 0, 0, 0.08)'
            }
        }
    },
    props: {
        MuiButtonBase: {
            disableRipple: true
        }
    }
});

export default theme;

